<script lang="ts">
import { defineComponent, ref, watch, type PropType } from 'vue'
import type { InputSelectOption } from '@/types/ui/inspection/common/inspectionSelectOptions'
import PhotographyComp from '@/components/inspection/common/PhotographyComp.vue'
import ImageThumbnail from '@/components/inspection/common/ImageThumbnail.vue'
import PhotoDBOperator from '@/accessors/indexedDB/photoDBOperator'
import type { FileObject } from '@/types/accessor/indexedDB/disasterEmergencyPhotoDB'
import { useDisasterStore } from '@/stores/disasterStore'
import { useInspectionResultStore } from '@/stores/inspectionResultUpsertStore'

export interface InspectionFieldData {
  index?: number
  title: string
  inputSelectOption: InputSelectOption[]
}

export default defineComponent({
  name: 'LabeledCard',
  components: {
    PhotographyComp,
    ImageThumbnail
  },
  props: {
    inspectionFieldData: {
      type: Object as PropType<InspectionFieldData>,
      required: true
    },
    targetActionKey: {
      type: String,
      required: true
    },
    s3DirectoryName: {
      type: String,
      required: true
    },
    imageCountLimit: {
      type: Number,
      required: true
    },
    imageList: {
      type: Array as PropType<File[]>,
      required: true,
      default: () => []
    },
    imagePathList: {
      type: Array as () => string[],
      required: true
    },
    hideSelectBox: {
      type: Boolean,
      default: false
    },
    index: {
      type: Number,
      required: false
    }
  },
  emits: ['update:modelValue', 'updateImagePathList', 'updateImageList'],
  computed: {},
  setup(props, { emit }) {
    const disasterStore = useDisasterStore()
    const inspectionResultStore = useInspectionResultStore()

    const imageListRef = ref<File[]>(props.imageList)
    const imagePathList = ref<string[]>(props.imagePathList)

    /**
     * 画像が選択（撮影）された際に実行する
     * @param value 画像データ
     * @returns なし
     */
    const onImageSelected = async (value: FileObject) => {
      // 画像リストのref変数を更新
      imageListRef.value = [...imageListRef.value, value.image]

      const photoData = await PhotoDBOperator.insertPhoto(value, {
        govCode: disasterStore.govCode!,
        disasterId: disasterStore.disasterId!,
        assessmentId: inspectionResultStore.commonResultSummary.assessmentId,
        field: props.s3DirectoryName
      })

      // ファイル名リストを更新
      imagePathList.value = [...imagePathList.value, photoData.imageFilePathList[0]]
      emit('updateImagePathList', {
        imagePathList: imagePathList.value,
        key: props.targetActionKey
      })
    }

    /**
     * 画像削除処理
     * @param index 削除対象の画像インデックス
     * @returns なし
     * @note このタイミングではIndexedDBのフラグ更新にとどめる（実際の物理削除は調査保存時）
     */
    const deleteImage = async (index: number): Promise<void> => {
      await PhotoDBOperator.updateStateToDelete(
        [
          disasterStore.govCode!,
          disasterStore.disasterId!,
          inspectionResultStore.commonResultSummary.assessmentId,
          props.s3DirectoryName,
          imagePathList.value[index].replace(/\.[^/.]+$/, '')
        ].join('/')
      )

      imagePathList.value.splice(index, 1)
      imageListRef.value.splice(index, 1)

      emit('updateImagePathList', {
        imagePathList: imagePathList.value,
        key: props.targetActionKey
      })
      emit('updateImageList', {
        imageList: imageListRef.value
      })
    }

    // 親コンポーネントでの変更を検知して同期するためのwatch
    watch(
      () => props.imageList,
      newValue => {
        imageListRef.value = [...newValue]
      }
    )

    watch(
      () => props.imagePathList,
      newValue => {
        imagePathList.value = [...newValue]
      }
    )

    return {
      imageListRef,
      onImageSelected,
      deleteImage
    }
  }
})
</script>

<template>
  <div class="border border-secondary rounded-lg p-4 mb-4 bg-white shadow rounded-3">
    <!-- ヘッダー部分 -->
    <div class="d-flex align-items-center gap-4 mb-4">
      <div class="custom-heading">{{ inspectionFieldData.title }}</div>
      <!-- 個別の拡張要素 -->
      <slot name="header-extension"></slot>
    </div>
    <!-- プルダウンとカメラボタン -->
    <div class="d-flex align-items-center gap-4 mb-4 justify-content-between">
      <!-- 判定結果 -->
      <slot name="overall-result"></slot>
      <!-- プルダウン -->
      <div class="d-flex align-items-center gap-2 flex-grow fs-5" v-if="!hideSelectBox">
        <div class="shadow-sm">
          <slot name="input-field"></slot>
        </div>
      </div>
      <!-- カメラボタン -->
      <PhotographyComp
        :emitName="'onImageSelected'"
        :itemName="s3DirectoryName"
        :imageCount="imageListRef.length"
        :imageCountLimit="imageCountLimit"
        @onImageSelected="onImageSelected"
      />
    </div>
    <!-- 個別の拡張要素 -->
    <slot name="extension"></slot>
    <!-- サムネイルリスト -->
    <ImageThumbnail :imageList="imageListRef" @deleteImage="deleteImage" />
    <!-- コメント入力欄スロット -->
    <div class="mt-4 shadow-inner">
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<style scoped>
.input-width {
  width: 300px;
}

.custom-heading {
  font-size: 1.75rem;
  font-weight: bold;
}
</style>
